import "./DriverRegistration.scss"
import React, { useEffect, useState } from "react"
import {
  Button,
  Box,
  Typography,
  ImageList,
  ImageListItem,
  FormControlLabel,
  Switch,
  Tooltip,
  FormHelperText,
} from "@mui/material"
import useFormValidation from "../../hooks/useFormValidation"
import { useTranslationLabel } from "../../hooks/useTranslationLabel"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { ModalTypes, showModal } from "../../redux/slices/modalSlice"
import AppInput from "../../components/AppInput/AppInput.component"
import cameraIcon from "../../assets/images/camera-icon.png"
import cameraBackIcon from "../../assets/images/camera-back.png"
import selfieIcon from "../../assets/images/selfie-icon.png"
import {
  completeAddDriverAsync,
  getRegisteredDriverAsync,
  selectRegistrationDrivrData,
  uploadDocumentAsync,
} from "../../redux/slices/driverSlice"
import { useParams } from "react-router-dom"
import WarningIcon from "../../assets/icons/WarningIcon"
import { useValidationSchemas } from "../../hooks/useIsValidationScehemes"

const DriverRegistration = () => {
  const { token } = useParams<{ token: string }>()
  const driverData = useAppSelector(selectRegistrationDrivrData)
  const dispatch = useAppDispatch()
  const [imageUploadError, setImageUploadError] = useState("")

  const {
    DRIVER_REGISTRATION_TITLE,
    DRIVER_REGISTRATION_DESCRIPTION_1,
    DRIVER_REGISTRATION_DESCRIPTION_2,
    DRIVER_REGISTRATION_23_DISCLAIMER,
    DRIVER_LICENSE_FRONT_INPUT_TITLE,
    DRIVER_LICENSE_BACK_INPUT_TITLE,
    ID_OR_PASSPORT_FRONT_INPUT_TITLE,
    DRIVER_LICENSE_SELFIE_INPUT_TITLE,
    INPUT_CITY,
    INPUT_STREET_AND_HOUSE_NUM,
    INPUT_POSTAL_CODE,
    INPUT_PASSWORD,
    INPUT_CONFIRM_PASSWORD,
    INPUT_PASSWORD_HINT,
    INPUT_PINCODE,
    BTN_CONFIRM,
    DRIVER_REGISTRATION_PREFERENCE_UNDER_23,
    DRIVER_REGISTRATION_PRIVACY_POLICY_1,
    DRIVER_REGISTRATION_PRIVACY_POLICY_2,
    DRIVER_REGISTRATION_TERMS_AND_CONDITIONS_1,
    DRIVER_REGISTRATION_TERMS_AND_CONDITIONS_2,
    DRIVER_REGISTRATION_SPECIAL_OFFERS_1,
    DRIVER_REGISTRATION_SPECIAL_OFFERS_2,
    TERMS_AND_CONDITIONS,
    DRIVER_REGISTRATION_SUCCESS_DESCRIPTION_1,
    DRIVER_REGISTRATION_SUCCESS_DESCRIPTION_2,
    SIMPLE_MODAL_ERROR_TITLE,
    SIMPLE_MODAL_ERROR_DESCRIPTION,
    IMAGES_REQUIRED,
  } = useTranslationLabel()
  const { driverRegistrationSchema } = useValidationSchemas()
  useEffect(() => {
    if (!token) return

    const initializeDriver = async () => {
      await dispatch(getRegisteredDriverAsync(token))
    }

    initializeDriver()
  }, [token, dispatch])

  const filesContainerList = [
    {
      id: "drivers-license-front",
      text: DRIVER_LICENSE_FRONT_INPUT_TITLE,
      image: <img src={cameraIcon} className="input-icon" alt="" />,
      documentType: 0,
    },
    {
      id: "drivers-license-back",
      text: DRIVER_LICENSE_BACK_INPUT_TITLE,
      image: <img src={cameraBackIcon} className="input-icon" alt="" />,
      documentType: 1,
    },
    {
      id: "id-card-front",
      text: ID_OR_PASSPORT_FRONT_INPUT_TITLE,
      image: <img src={cameraIcon} className="input-icon" alt="" />,
      documentType: 50,
    },
    {
      id: "selfie",
      text: DRIVER_LICENSE_SELFIE_INPUT_TITLE,
      image: <img src={selfieIcon} className="input-icon" alt="" />,
      documentType: 37,
    },
  ] as const

  const { formData, errors, handleChange, validateForm } = useFormValidation(
    {
      city: "",
      street: "",
      postalCode: "",
      pinCode: "",
      password: "",
      confirmPassword: "",
      under23AndInexperienced: false,
      agreesToPrivacyPolicy: false,
      acknowledgesTerms: false,
      wantsSpecialOffers: false,
    },
    driverRegistrationSchema,
  )

  const [images, setImages] = useState<{ file: File; payload: number }[]>([])

  useEffect(() => {
    if (formData.under23AndInexperienced) {
      dispatch(
        showModal({
          activeModal: ModalTypes.SIMPLE_MODAL,
          data: { description: DRIVER_REGISTRATION_23_DISCLAIMER },
        }),
      )
    }
  }, [
    formData.under23AndInexperienced,
    dispatch,
    DRIVER_REGISTRATION_23_DISCLAIMER,
  ])

  const handleImageChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
    item: any,
    index: number,
  ) => {
    if (event.target.files && event.target.files[0]) {
      const file = event.target.files[0]
      const base64 = await convertToBase64(file)
      const res = await dispatch(
        uploadDocumentAsync({ base64, documentType: item.documentType }),
      )
      if (res?.payload) {
        const newImages = [...images]
        newImages[index] = { file, payload: res.payload }
        setImages(newImages)
      }
    }
  }

  const convertToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader()
      fileReader.readAsDataURL(file)
      fileReader.onload = () => {
        resolve(fileReader.result as string)
      }
      fileReader.onerror = (error) => {
        reject(error)
      }
    })
  }

  const handleSubmit = async () => {
    if (images.length < filesContainerList.length) {
      setImageUploadError(IMAGES_REQUIRED)
      return
    }
    setImageUploadError("")

    if (validateForm()) {
      dispatch(completeAddDriverAsync({ formData, images }))
        .unwrap()
        .then(() => {
          dispatch(
            showModal({
              activeModal: ModalTypes.SIMPLE_MODAL,
              data: {
                className: "driver-register-success",
                description: DRIVER_REGISTRATION_SUCCESS_DESCRIPTION_1,
                description2: DRIVER_REGISTRATION_SUCCESS_DESCRIPTION_2,
              },
            }),
          )
        })
        .catch((err) => {
          dispatch(
            showModal({
              activeModal: ModalTypes.SIMPLE_MODAL,
              data: {
                title: SIMPLE_MODAL_ERROR_TITLE,
                description: SIMPLE_MODAL_ERROR_DESCRIPTION,
                image: <WarningIcon />,
              },
            }),
          )
        })
    }
  }

  if (!driverData) return null
  return (
    <Box className="driver-registration-container full">
      <Typography variant="h4" className="title center">
        {DRIVER_REGISTRATION_TITLE}
      </Typography>
      <Typography variant="body1" className="body1 center">
        {DRIVER_REGISTRATION_DESCRIPTION_1}
        <br />
        {DRIVER_REGISTRATION_DESCRIPTION_2}
      </Typography>

      <ImageList cols={2} rowHeight={130} className="image-list-custom full">
        {filesContainerList.map((item, index) => (
          <label
            htmlFor={`file-button-${index}`}
            key={item.id}
            className="upload-button"
          >
            <input
              accept="image/*"
              style={{ display: "none" }}
              id={`file-button-${index}`}
              type="file"
              onChange={(e) => handleImageChange(e, item, index)}
            />
            <ImageListItem className="file-input flex center space-between">
              {images[index] ? (
                <img
                  src={URL.createObjectURL(images[index].file)}
                  alt={item.text}
                  className="uploaded-img"
                />
              ) : (
                <div className="empty flex center full">
                  <span className="material-icons">{item.image}</span>
                  <p>{item.text}</p>
                </div>
              )}
            </ImageListItem>
          </label>
        ))}
      </ImageList>
      <FormHelperText className="error-message">
        {imageUploadError}
      </FormHelperText>

      <Box className="form-container space-between">
        <AppInput
          label={INPUT_CITY}
          name="city"
          value={formData.city}
          error={errors.city}
          onChange={handleChange}
        />

        <AppInput
          label={INPUT_STREET_AND_HOUSE_NUM}
          name="street"
          value={formData.street}
          error={errors.street}
          onChange={handleChange}
        />

        <AppInput
          label={INPUT_POSTAL_CODE}
          name="postalCode"
          value={formData.postalCode}
          error={errors.postalCode}
          onChange={handleChange}
        />

        <AppInput
          label={INPUT_PINCODE}
          name="pinCode"
          value={formData.pinCode}
          error={errors.pinCode}
          onChange={handleChange}
        />

        <AppInput
          label={INPUT_PASSWORD}
          type="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          error={errors.password}
          endAdornment={
            <Tooltip title={INPUT_PASSWORD_HINT} placement="top" arrow>
              <div className="tooltip">{"\u003F"}</div>
            </Tooltip>
          }
        />

        <AppInput
          label={INPUT_CONFIRM_PASSWORD}
          type="password"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleChange}
          error={errors.confirmPassword}
        />
      </Box>
      <Box className="options column">
        {errors.agreesToPrivacyPolicy && (
          <FormHelperText className="error-message">
            {errors.agreesToPrivacyPolicy}
          </FormHelperText>
        )}
        {errors.acknowledgesTerms && (
          <FormHelperText className="error-message">
            {errors.acknowledgesTerms}
          </FormHelperText>
        )}
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.under23AndInexperienced}
              onChange={(e) => handleChange(e, "checkbox")}
              name="under23AndInexperienced"
            />
          }
          label={
            <Typography variant="body1" className="body1 bold">
              {DRIVER_REGISTRATION_PREFERENCE_UNDER_23}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.agreesToPrivacyPolicy}
              onChange={(e) => handleChange(e, "checkbox")}
              name="agreesToPrivacyPolicy"
            />
          }
          label={
            <Typography variant="body1" className="body1 bold">
              {DRIVER_REGISTRATION_PRIVACY_POLICY_1}&nbsp;
              <a
                className="link"
                href="https://gotoglobal.com/en/goto-spain/privacy-policy"
                target="_blank"
              >
                {DRIVER_REGISTRATION_PRIVACY_POLICY_2}
              </a>
            </Typography>
          }
        />

        <FormControlLabel
          control={
            <Switch
              checked={!!formData.acknowledgesTerms}
              onChange={(e) => handleChange(e, "checkbox")}
              name="acknowledgesTerms"
            />
          }
          label={
            <Typography variant="body1" className="body1 bold">
              {DRIVER_REGISTRATION_TERMS_AND_CONDITIONS_1}&nbsp;
              <a
                className="link"
                href="https://gotoglobal.com/en/country/spain"
                target="_blank"
              >
                {TERMS_AND_CONDITIONS}
              </a>
              &nbsp;
              {DRIVER_REGISTRATION_TERMS_AND_CONDITIONS_2}
            </Typography>
          }
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!formData.wantsSpecialOffers}
              onChange={(e) => handleChange(e, "checkbox")}
              name="wantsSpecialOffers"
            />
          }
          label={
            <>
              <Typography variant="body1" className="body1 bold">
                {DRIVER_REGISTRATION_SPECIAL_OFFERS_1}
              </Typography>
              <Typography variant="body1" className="body1 bold sm">
                {DRIVER_REGISTRATION_SPECIAL_OFFERS_2}
              </Typography>
            </>
          }
        />
      </Box>

      <Button
        className="primary-btn"
        variant="contained"
        onClick={handleSubmit}
      >
        {BTN_CONFIRM}
      </Button>
    </Box>
  )
}

export default DriverRegistration
